import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, humanizeClientError } from "@/lib/api";
import {
  chatAccountLabel,
  chatGroupTitle,
  fetchChatAccounts,
  type ChatAccount,
  type ChatMessage,
} from "@/lib/chatConnect";
import { replaceShortcodes, useSlackEmoji } from "@/lib/emoji";
import { formatListWhen } from "@/lib/listWhen";
import { peekModuleCache, writeModuleCache } from "@/lib/moduleCache";
import {
  NEEDS_YOU_PILOT_MESSAGE,
  fetchSavedFromSlack,
  fetchSavedMessage,
  isListedSlackCustomer,
  isSlackCustomerAccount,
  removeSavedFromSlack,
  slackRetryAfter,
  useNeedsYou,
  type NeedsYouItem,
  type SavedFromSlack,
} from "@/lib/slackCustomer";
import { readMailClockFormat } from "@/mail/prefs";

const NEEDS_YOU_SHOWN = 8;
const SLACK_HOME_CACHE_KEY = "home:slack";

type SlackHomeSnapshot = { accounts: ChatAccount[]; saved: SavedFromSlack[] };

type SavedLoad =
  | { state: "loading" | "waiting" | "missing" | "disconnected" }
  | { state: "ready"; message: ChatMessage }
  | { state: "error"; error: string };

/** Hub Home's panels for Slack accounts connected through the ATW Hub app (Tip 148). */
export function SlackHomePanels() {
  const [accounts, setAccounts] = useState<ChatAccount[] | null>(
    () => peekModuleCache<SlackHomeSnapshot>(SLACK_HOME_CACHE_KEY)?.accounts ?? null,
  );
  const [saved, setSaved] = useState<SavedFromSlack[] | null>(
    () => peekModuleCache<SlackHomeSnapshot>(SLACK_HOME_CACHE_KEY)?.saved ?? null,
  );
  const [savedError, setSavedError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetchChatAccounts().then((rows) => rows.filter(isSlackCustomerAccount)),
      fetchSavedFromSlack().catch((err) => {
        if (!cancelled) setSavedError(humanizeClientError(err, "Could not load your saved Slack messages."));
        return [] as SavedFromSlack[];
      }),
    ]).then(([customers, items]) => {
      if (cancelled) return;
      setAccounts(customers);
      setSaved(items);
      void writeModuleCache<SlackHomeSnapshot>(
        SLACK_HOME_CACHE_KEY,
        { accounts: customers, saved: items },
        { mirror: true },
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!accounts || !saved) return null;
  if (!accounts.length && !saved.length && !savedError) return null;

  return (
    <div className="hub-home-split hub-slack-split">
      {accounts.length ? <NeedsYouPanel accounts={accounts} /> : null}
      <SavedFromSlackPanel items={saved} error={savedError} />
    </div>
  );
}

function needsWhere(item: NeedsYouItem): string {
  if (item.kind === "dm") {
    return item.channelName?.startsWith("mpdm-") ? chatGroupTitle(item.channelName) : "Direct message";
  }
  return item.channelName ? `#${item.channelName}` : "Slack";
}

function NeedsYouPanel({ accounts }: { accounts: ChatAccount[] }) {
  useSlackEmoji();
  const needs = useNeedsYou(accounts, true);
  const listed = accounts.filter(isListedSlackCustomer);
  const clock = readMailClockFormat();
  const rows = listed
    .flatMap((account) => (needs.snapshots[account.id]?.items || []).map((item) => ({ account, item })))
    .sort((a, b) => (b.item.at ?? 0) - (a.item.at ?? 0));
  const problems = [
    ...Object.values(needs.errors),
    ...listed.map((a) => needs.snapshots[a.id]?.partialError).filter((m): m is string => Boolean(m)),
  ];

  return (
    <section className="settings-card hub-panel" id="hub-needs-you">
      <div className="settings-card-head">
        <h2>Needs you</h2>
        <Link className="hub-panel-link" to="/chat">
          Open Chat
        </Link>
      </div>
      {listed.length < accounts.length ? <p className="muted">{NEEDS_YOU_PILOT_MESSAGE}</p> : null}
      {problems.map((message) => (
        <p key={message} className="banner-error hub-panel-banner">
          {message}
        </p>
      ))}
      {!listed.length ? null : needs.loading && !rows.length ? (
        <p className="muted">Checking Slack…</p>
      ) : !rows.length ? (
        problems.length ? null : <p className="muted">Nothing in Slack needs you right now.</p>
      ) : (
        <ul className="hub-plain-list">
          {rows.slice(0, NEEDS_YOU_SHOWN).map(({ account, item }) => {
            const meta = [
              needsWhere(item),
              item.threadTs ? "in a thread" : "",
              accounts.length > 1 ? chatAccountLabel(account) : "",
              formatListWhen(item.at, clock),
            ]
              .filter(Boolean)
              .join(" · ");
            const body = (
              <>
                <strong>{item.author}</strong>
                <span>{meta}</span>
                {item.text ? <span className="hub-slack-text">{replaceShortcodes(item.text)}</span> : null}
              </>
            );
            return (
              <li key={`${account.id}:${item.channelId}:${item.ts}`}>
                {/* Hub Chat shows conversations, not threads: a thread reply opens in Slack. */}
                {item.threadTs && item.permalink ? (
                  <a href={item.permalink}>{body}</a>
                ) : (
                  <Link to={`/chat?open=${encodeURIComponent(`${account.id}:${item.channelId}`)}`}>{body}</Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function savedStatusText(load: SavedLoad | undefined, item: SavedFromSlack): string {
  switch (load?.state) {
    case "waiting":
      return "Waiting for Slack…";
    case "missing":
      return "This message isn't in Slack anymore, or you can't see it.";
    case "disconnected":
      return `Connect ${item.teamName || "this Slack workspace"} to ATW Hub again to see this message.`;
    case "error":
      return load.error;
    default:
      return "Loading from Slack…";
  }
}

function SavedFromSlackPanel({ items, error }: { items: SavedFromSlack[]; error: string }) {
  const [loads, setLoads] = useState<Record<string, SavedLoad>>({});
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [removing, setRemoving] = useState("");
  const [removeError, setRemoveError] = useState("");
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [clock, setClock] = useState(() => Date.now());
  const loadsRef = useRef<Record<string, SavedLoad>>({});
  const removedRef = useRef<ReadonlySet<string>>(removed);
  const clockFormat = readMailClockFormat();

  // One saved message at a time, live from Slack; a rate limit pauses the rest until Slack allows more.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const put = (id: string, load: SavedLoad) => {
      loadsRef.current = { ...loadsRef.current, [id]: load };
      setLoads(loadsRef.current);
    };
    const settled = (id: string) => {
      const state = loadsRef.current[id]?.state;
      return state === "ready" || state === "missing" || state === "disconnected" || state === "error";
    };
    async function run() {
      for (const item of items) {
        if (cancelled) return;
        if (removedRef.current.has(item.id) || settled(item.id)) continue;
        if (!item.connected) {
          put(item.id, { state: "disconnected" });
          continue;
        }
        put(item.id, { state: "loading" });
        try {
          const message = await fetchSavedMessage(item.id);
          if (cancelled) return;
          put(item.id, message ? { state: "ready", message } : { state: "missing" });
        } catch (err) {
          if (cancelled) return;
          const wait = slackRetryAfter(err);
          if (wait != null) {
            put(item.id, { state: "waiting" });
            setRetryAt(Date.now() + wait * 1000);
            timer = window.setTimeout(() => {
              setRetryAt(null);
              setRetryNonce((n) => n + 1);
            }, wait * 1000);
            return;
          }
          if (err instanceof ApiError && err.status === 404) {
            removedRef.current = new Set(removedRef.current).add(item.id);
            setRemoved(removedRef.current);
            continue;
          }
          put(
            item.id,
            err instanceof ApiError && err.status === 409
              ? { state: "disconnected" }
              : { state: "error", error: humanizeClientError(err, "Could not load this message from Slack.") },
          );
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [items, retryNonce]);

  useEffect(() => {
    if (!retryAt) return;
    setClock(Date.now());
    const tick = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [retryAt]);

  async function remove(item: SavedFromSlack) {
    setRemoving(item.id);
    setRemoveError("");
    try {
      await removeSavedFromSlack(item.id);
      removedRef.current = new Set(removedRef.current).add(item.id);
      setRemoved(removedRef.current);
    } catch (err) {
      setRemoveError(humanizeClientError(err, "Could not remove it. Try again."));
    } finally {
      setRemoving("");
    }
  }

  const visible = items.filter((item) => !removed.has(item.id));
  const waitSeconds = retryAt ? Math.max(1, Math.ceil((retryAt - clock) / 1000)) : 0;

  return (
    <section className="settings-card hub-panel" id="hub-saved-slack">
      <div className="settings-card-head">
        <h2>Saved from Slack</h2>
      </div>
      {error ? <p className="banner-error hub-panel-banner">{error}</p> : null}
      {removeError ? <p className="banner-error hub-panel-banner">{removeError}</p> : null}
      {waitSeconds ? (
        <p className="muted">
          Slack is limiting how often ATW Hub can load saved messages. Loading the rest in {waitSeconds}{" "}
          second{waitSeconds === 1 ? "" : "s"}.
        </p>
      ) : null}
      {!visible.length ? (
        error ? null : (
          <p className="muted">
            In Slack, choose Send to Hub from a message&apos;s More actions menu to save a link to it here.
          </p>
        )
      ) : (
        <ul className="hub-plain-list">
          {visible.map((item) => {
            const load = loads[item.id];
            const savedWhen = formatListWhen(item.savedAt, clockFormat);
            return (
              <li key={item.id} className="hub-slack-saved">
                {load?.state === "ready" ? (
                  <>
                    <strong>{load.message.author}</strong>
                    <span className="hub-slack-text">{load.message.text || "(no text)"}</span>
                  </>
                ) : (
                  <span className="hub-slack-text muted">{savedStatusText(load, item)}</span>
                )}
                <span>{[item.teamName, savedWhen ? `Saved ${savedWhen}` : ""].filter(Boolean).join(" · ")}</span>
                <div className="hub-slack-saved-actions">
                  <a className="hub-panel-link" href={item.permalink}>
                    Open in Slack
                  </a>
                  <button
                    type="button"
                    className="link-btn"
                    disabled={removing === item.id}
                    onClick={() => void remove(item)}
                  >
                    {removing === item.id ? "Removing…" : "Remove"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
